import { useCallback, useEffect, useId, useRef, useState } from 'react'

/**
 * La colonne « Champs du contenu » de l'éditeur d'un contenu dans son modèle :
 * une bande collée sous la barre, À DÉFILEMENT PROPRE et REDIMENSIONNABLE.
 *
 * POURQUOI DU JS ICI (justification config-first) : il ne s'agit d'aucune
 * donnée — ni YAML reactive-json ni capacité Drupal ne décrit la largeur d'un
 * panneau tirée à la souris, sa persistance d'une session à l'autre, ni la
 * hauteur restante sous une barre dont la hauteur se mesure. C'est le chrome
 * d'un écran déjà React (l'éditeur de contenu), et c'est UN composant partagé
 * plutôt qu'un bloc inline : la mécanique tient en un endroit.
 *
 * Défilement propre : le canevas continue de défiler avec le DOCUMENT (c'est la
 * règle du builder — un ascenseur imbriqué casserait les blocs `sticky` d'un
 * modèle), et la colonne, elle, se colle et défile chez elle. On atteint donc
 * le dernier champ d'un contenu long sans avoir à faire défiler la page entière
 * jusqu'en bas.
 *
 * La poignée est un `separator` ACTIONNABLE AU CLAVIER (flèches, Origine/Fin) :
 * un redimensionnement réservé à la souris serait une commande sans équivalent
 * clavier (WCAG 2.1.1). Sa largeur est mémorisée par navigateur —
 * `localStorage` peut lever (navigation privée, données de site bloquées), d'où
 * les try/catch : un échec rend simplement la largeur par défaut.
 */

const STORAGE_KEY = 'orbit.builder.objectFieldsPanelWidth'
const MIN_WIDTH = 280
const MAX_WIDTH = 860
const DEFAULT_WIDTH = 340
const STEP = 24

const clamp = (value) => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(value)))

const readStoredWidth = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    const parsed = Number(raw)
    return Number.isFinite(parsed) && parsed > 0 ? clamp(parsed) : DEFAULT_WIDTH
  } catch {
    return DEFAULT_WIDTH
  }
}

export const BuilderObjectFieldsPanel = ({ id, top = 0, label, children }) => {
  const [width, setWidth] = useState(readStoredWidth)
  const handleId = useId()
  // Le geste en cours : largeur et abscisse au `pointerdown`. Dans une ref —
  // le déplacement ne doit pas dépendre d'un rendu pour lire sa référence.
  const drag = useRef(null)

  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, String(width)) } catch { /* stockage refusé : la largeur ne survivra pas à la session */ }
  }, [width])

  const onPointerDown = useCallback((e) => {
    // Bouton principal seulement : un clic droit ou un clic du milieu
    // n'engagent pas un glissement.
    if (e.button !== 0) return
    e.preventDefault()
    drag.current = { startX: e.clientX, startWidth: width }
    e.currentTarget.setPointerCapture?.(e.pointerId)
  }, [width])

  const onPointerMove = useCallback((e) => {
    if (!drag.current) return
    // La colonne est à DROITE : tirer vers la gauche l'élargit.
    setWidth(clamp(drag.current.startWidth + (drag.current.startX - e.clientX)))
  }, [])

  const endDrag = useCallback((e) => {
    if (!drag.current) return
    drag.current = null
    e.currentTarget.releasePointerCapture?.(e.pointerId)
  }, [])

  const onKeyDown = useCallback((e) => {
    const keys = {
      ArrowLeft: w => w + STEP,
      ArrowRight: w => w - STEP,
      Home: () => MIN_WIDTH,
      End: () => MAX_WIDTH,
    }
    const next = keys[e.key]
    if (!next) return
    e.preventDefault()
    setWidth(w => clamp(next(w)))
  }, [])

  return (
    <div
      className="sticky self-start shrink-0 flex bg-gray-50"
      style={{ top, width, height: `calc(100vh - ${top}px)` }}
    >
      <div
        id={handleId}
        role="separator"
        aria-orientation="vertical"
        aria-label="Largeur du panneau des champs"
        aria-controls={id}
        aria-valuenow={width}
        aria-valuemin={MIN_WIDTH}
        aria-valuemax={MAX_WIDTH}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={onKeyDown}
        className="w-1.5 shrink-0 cursor-col-resize bg-gray-200 hover:bg-teal-400 focus-visible:outline-none focus-visible:bg-teal-500 transition-colors touch-none"
      />
      <aside
        id={id}
        aria-label={label}
        // `tabindex=-1` + défilement propre : le panneau est une région
        // défilante, et une région qui défile doit être atteignable au clavier
        // pour qu'on puisse la parcourir (WCAG 2.1.1).
        tabIndex={-1}
        className="flex-1 min-w-0 overflow-y-auto overscroll-contain"
      >
        {children}
      </aside>
    </div>
  )
}

export default BuilderObjectFieldsPanel
